import type * as CSS from "csstype"

type FontStyle = {
    fontFamily: string
    fontSize: CSS.Property.FontSize
    fontWeight: CSS.Property.FontWeight
    letterSpacing?: CSS.Property.LetterSpacing
    lineHeight: CSS.Property.LineHeight
    textTransform?: CSS.Property.TextTransform
    fontStyle?: CSS.Property.FontStyle
}

/**
 * First initiative to define font styles in a single place
 * to avoid duplication and inconsistency
 */

/**
 * Gothic fonts
 * TradeGothicLTPro-Light (300, normal)
 * TradeGothicLTPro-BdCn20 (700, bold)
 * TradeGothicLTPro-BdCn20 (700, bold-condensed)
 *
 * Trim fonts
 * TrimMonoPOC-Light (300)
 * TrimMonoPOC-Regular (400)
 *
 */

export type FontStyles =
    | ".font_trimMonoPoc_Regular_16_120_uppercase"
    | ".font_trimMonoPoc_Regular_15_130_uppercase"
    | ".font_trimMonoPoc_Regular_14_120_uppercase"
    | ".font_trimMonoPoc_Regular_14_120"
    | ".font_trimMonoPoc_Regular_12_120_uppercase"
    | ".Trim_Mono_Regular_POC_Header_32_130_1_400_uppercase"
    | ".Trim_Mono_POC_Text_14_130_0_400_uppercase"
    | ".Trim_Mono_POC_Text_18_130_-3_500_uppercase"
    | ".font_tradeGothicLTPro_Light_32_120"
    | ".font_tradeGothicLTPro_Light_16_130"
    | ".Trade_Gothic_Header_96_100_-1_300"
    | ".Trade_Gothic_Header_64_100_-1_300"
    | ".Trade_Gothic_Header_56_100_0_300"
    | ".Trade_Gothic_Header_48_105_0_300"
    | ".Trade_Gothic_Header_40_105_0_300"
    | ".Trade_Gothic_Header_32_105_0_300"
    | ".Trade_Gothic_Header_24_125_0_300"
    | ".Trade_Gothic_Header_24_145_0_300"
    | ".Trade_Gothic_Text_14_140_0_300"
    | ".Trade_Gothic_Text_14_150_0_300"
    | ".Trade_Gothic_Text_14_150_0_700"
    | ".Trade_Gothic_Text_16_145_0_300"
    | ".Trade_Gothic_Text_16_150_0_300"
    | ".Trade_Gothic_Text_16_150_0_700"
    | ".Trade_Gothic_Text_Bold_16_150_0_700"
    | ".Trade_Gothic_Text_Bold_14_150_0_700"
    | ".Trade_Gothic_Text_Bold_14_150_0_300"
    | ".Trade_Gothic_Text_12_150_0_300"
    | ".Trade_Gothic_Text_12_150_0_300_uppercase"
    | ".Trade_Gothic_Text_10_150_0_300"
    | ".Trim_Mono_POC_Text_10_130_-3_400_uppercase"
    | ".Trim_Mono_POC_Text_12_130_-3_400_uppercase"
    | ".Trim_Mono_POC_Text_18_130_-3_300_uppercase"
    | ".Trim_Mono_POC_Text_16_130_0_300_uppercase"
    | ".Trim_Mono_POC_Text_15_130_7_500_uppercase"
    | ".Trim_Mono_POC_Text_15_130_0_300_uppercase"
    | ".Trim_Mono_POC_Text_14_130_0_500_uppercase"
    | ".Trim_Mono_POC_Text_14_130_-5_500_uppercase"
    | ".Trim_Mono_POC_Text_16_130_-5_500_uppercase"
    | ".Trim_Mono_POC_Text_20_130_-5_400_uppercase"
    | ".Trim_Mono_POC_Text_14_130_-5_400_uppercase"
    | ".Trim_Mono_POC_Text_14_130_-5_300_uppercase"
    | ".Trim_Mono_POC_Text_14_130_-4_400_uppercase"
    | ".Trim_Mono_POC_Text_12_130_0_400_uppercase"
    | ".Trim_Mono_POC_Text_12_130_6_400_uppercase"
    | ".Trim_Mono_POC_Text_12_130_0_300_uppercase"
    | ".Trim_Mono_POC_Text_12_130_6_500_uppercase"
    | ".Trim_Mono_Medium_POC_Text_12_130_-5_500_uppercase"
    | ".Trim_Mono_POC_Text_10_130_3_400_uppercase"
    | ".Trim_Mono_POC_Text_32_130_-4_400_uppercase"
    | ".Trade_Gothic_Header_96_100_-1_700_uppercase"
    | ".Trade_Gothic_Header_64_100_-1_700_uppercase"
    | ".Trade_Gothic_Header_56_100_0_700_uppercase"
    | ".Trade_Gothic_Header_48_105_0_700_uppercase"
    | ".Trade_Gothic_Header_40_105_0_700_uppercase"
    | ".Trade_Gothic_Header_32_105_0_700_uppercase"
    | ".Trade_Gothic_Header_24_125_0_700_uppercase"
    | ".Trade_Gothic_Header_24_145_0_700_uppercase"

/**
 * Utility type that removes the leading dot from FontStyles
 * Use this when you need to use FontStyles as className values (without the dot prefix)
 */
export type FontStylesWithoutDot<T extends string = FontStyles> =
    T extends `.${infer Rest}` ? Rest : T

export const fontStyles: { [key: string]: FontStyle } = {
    // Regular fonts
    ".font_trimMonoPoc_Header_24_130_-4_400_uppercase": {
        fontFamily: "var(--font-trim-regular-new), sans-serif",
        fontSize: "1.5rem",
        fontWeight: 400,
        letterSpacing: "-0.04em",
        lineHeight: "130%",
        textTransform: "uppercase",
        fontStyle: "normal"
    },
    ".font_trimMonoPoc_Header_20_130_-4_400_uppercase": {
        fontFamily: "var(--font-trim-regular-new), sans-serif",
        fontSize: "1.25rem",
        fontWeight: 400,
        lineHeight: "130%",
        letterSpacing: "-0.05em",
        textTransform: "uppercase",
        fontStyle: "normal"
    },
    ".font_trimMonoPoc_Regular_16_120_uppercase": {
        fontFamily: "var(--font-trim-regular-new), sans-serif",
        fontSize: "1rem",
        fontWeight: 400,
        letterSpacing: "0",
        lineHeight: "120%",
        textTransform: "uppercase",
        fontStyle: "normal"
    },
    ".font_trimMonoPoc_Regular_15_130_uppercase": {
        fontFamily: "var(--font-trim-regular-new), sans-serif",
        fontSize: "0.9375rem",
        fontWeight: 400,
        letterSpacing: "0",
        lineHeight: "130%",
        textTransform: "uppercase",
        fontStyle: "normal"
    },
    ".Trim_Mono_POC_Text_15_130_-5_700_uppercase": {
        fontFamily: "var(--font-trim-regular-new), sans-serif",
        fontSize: "0.9375rem",
        fontWeight: 700,
        lineHeight: "130%",
        letterSpacing: "-0.05rem",
        textTransform: "uppercase"
    },
    ".font_trimMonoPoc_Regular_14_120_uppercase": {
        fontFamily: "var(--font-trim-regular-new), sans-serif",
        fontSize: "0.875rem",
        fontWeight: 400,
        letterSpacing: "0",
        lineHeight: "120%",
        textTransform: "uppercase",
        fontStyle: "normal"
    },
    ".font_trimMonoPoc_Regular_14_120": {
        fontFamily: "var(--font-trim-regular-new), sans-serif",
        fontSize: "0.875rem",
        fontWeight: 400,
        letterSpacing: "0",
        lineHeight: "120%",
        textTransform: "uppercase",
        fontStyle: "normal"
    },
    ".font_trimMonoPoc_Regular_12_120_uppercase": {
        fontFamily: "var(--font-trim-regular-new), sans-serif",
        fontSize: "0.75rem",
        fontWeight: 400,
        letterSpacing: "0",
        lineHeight: "120%",
        textTransform: "uppercase",
        fontStyle: "normal"
    },
    ".Trim_Mono_POC_Text_14_130_0_400_uppercase": {
        fontFamily: "var(--font-trim-regular-new), sans-serif",
        fontSize: "0.875rem",
        fontWeight: 400,
        lineHeight: "130%",
        letterSpacing: "0",
        textTransform: "uppercase"
    },
    ".Trim_Mono_POC_Text_16_130_0_400_uppercase": {
        fontFamily: "var(--font-trim-regular-new), sans-serif",
        fontSize: "1rem",
        fontWeight: 400,
        lineHeight: "130%",
        letterSpacing: "0",
        textTransform: "uppercase"
    },
    ".Trim_Mono_POC_Text_14_130_-5_500_uppercase": {
        fontFamily: "var(--font-trim-medium-new), sans-serif",
        fontSize: "0.875rem",
        fontWeight: 500,
        lineHeight: "130%",
        letterSpacing: "-0.05em",
        textTransform: "uppercase"
    },
    ".Trim_Mono_POC_Text_14_130_1_400_uppercase": {
        fontFamily: "var(--font-trim-regular-new), sans-serif",
        fontSize: "0.875rem",
        fontWeight: 400,
        lineHeight: "130%",
        letterSpacing: "-0.05em",
        textTransform: "uppercase"
    },
    ".Trim_Mono_POC_Text_14_130_-4_400_uppercase": {
        fontFamily: "var(--font-trim-regular-new), sans-serif",
        fontSize: "0.875rem",
        fontWeight: 400,
        lineHeight: "130%",
        letterSpacing: "-0.04em",
        textTransform: "uppercase"
    },
    ".Trim_Mono_Medium_POC_Text_12_130_-5_500_uppercase": {
        fontFamily: "var(--font-trim-medium-new), sans-serif",
        fontSize: "0.75rem",
        fontWeight: "500",
        lineHeight: "130%",
        letterSpacing: "-0.05rem",
        textTransform: "uppercase"
    },
    ".Trim_Mono_POC_Text_12_130_-3_500_uppercase": {
        fontFamily: "var(--font-trim-regular-new), sans-serif",
        fontSize: "0.75rem",
        fontWeight: 500,
        lineHeight: "130%",
        letterSpacing: "-0.0225rem",
        textTransform: "uppercase"
    },
    ".font_trimMonoPoc_Regular_10_130_-3_300_uppercase": {
        fontFamily: "var(--font-trim-regular-new), sans-serif",
        fontSize: "0.625rem",
        fontWeight: 300,
        letterSpacing: "-0.03em",
        lineHeight: "130%",
        textTransform: "uppercase",
        fontStyle: "normal"
    },
    ".Trim_Mono_POC_Text_18_130_-3_500_uppercase": {
        fontFamily: "var(--font-trim-medium-new), sans-serif",
        fontSize: "1.125em",
        fontWeight: 500,
        lineHeight: "130%",
        letterSpacing: "-0.03em",
        textTransform: "uppercase"
    },
    // Light fonts
    ".font_tradeGothicLTPro_Light_32_120": {
        fontFamily: "var(--font-trade-light-new), sans-serif",
        fontSize: "2rem",
        fontWeight: 300,
        letterSpacing: "0",
        lineHeight: "120%",
        textTransform: "none",
        fontStyle: "normal"
    },
    ".font_tradeGothicLTPro_Light_16_130": {
        fontFamily: "var(--font-trade-light-new), sans-serif",
        fontSize: "1rem",
        fontWeight: 300,
        letterSpacing: "0",
        lineHeight: "130%",
        textTransform: "none",
        fontStyle: "normal"
    },
    ".Trade_Gothic_Header_96_100_-1_300": {
        fontFamily: "var(--font-trade-light-new), sans-serif",
        fontSize: "6rem",
        fontWeight: 300,
        lineHeight: "100%",
        letterSpacing: "-0.01em"
    },
    ".Trade_Gothic_Header_64_100_-1_300": {
        fontFamily: "var(--font-trade-light-new), sans-serif",
        fontSize: "4rem",
        fontWeight: 300,
        lineHeight: "100%",
        letterSpacing: "-0.01em"
    },
    ".Trade_Gothic_Header_56_100_0_300": {
        fontFamily: "var(--font-trade-light-new), sans-serif",
        fontSize: "3.5rem",
        fontWeight: 300,
        lineHeight: "100%",
        letterSpacing: "0"
    },
    ".Trade_Gothic_Header_48_105_0_300": {
        fontFamily: "var(--font-trade-light-new), sans-serif",
        fontSize: "3rem",
        fontWeight: 300,
        lineHeight: "105%",
        letterSpacing: "0"
    },
    ".Trade_Gothic_Header_40_105_0_300": {
        fontFamily: "var(--font-trade-light-new), sans-serif",
        fontSize: "2.5rem",
        fontWeight: 300,
        lineHeight: "105%",
        letterSpacing: "0"
    },
    ".Trade_Gothic_Header_32_105_0_300": {
        fontFamily: "var(--font-trade-light-new), sans-serif",
        fontSize: "2rem",
        fontWeight: 300,
        lineHeight: "105%",
        letterSpacing: "0"
    },
    ".Trade_Gothic_Header_24_125_0_300": {
        fontFamily: "var(--font-trade-light-new), sans-serif",
        fontSize: "1.5rem",
        fontWeight: 300,
        lineHeight: "125%",
        letterSpacing: "0"
    },
    ".Trade_Gothic_Header_24_145_0_300": {
        fontFamily: "var(--font-trade-light-new), sans-serif",
        fontSize: "1.5rem",
        fontWeight: 300,
        lineHeight: "145%"
    },
    ".Trade_Gothic_Text_14_150_0_300": {
        fontFamily: "var(--font-trade-light-new), sans-serif",
        fontSize: "0.875rem",
        fontWeight: 300,
        lineHeight: "150%",
        letterSpacing: "0"
    },
    ".Trade_Gothic_Text_14_150_0_700": {
        fontFamily: "var(--font-trade-bold-new), sans-serif",
        fontSize: "0.875rem",
        fontWeight: 700,
        lineHeight: "150%",
        letterSpacing: "0"
    },
    ".Trade_Gothic_Text_16_145_0_300": {
        fontFamily: "var(--font-trade-light-new), sans-serif",
        fontSize: "1rem",
        fontWeight: 300,
        lineHeight: "145%",
        letterSpacing: "0"
    },
    ".Trade_Gothic_Text_16_150_0_300": {
        fontFamily: "var(--font-trade-light-new), sans-serif",
        fontSize: "1rem",
        fontWeight: 300,
        lineHeight: "150%",
        letterSpacing: "0"
    },
    ".Trade_Gothic_Text_16_150_0_700": {
        fontFamily: "var(--font-trade-bold-new), sans-serif",
        fontSize: "1rem",
        fontWeight: 700,
        lineHeight: "150%",
        letterSpacing: "0"
    },
    ".Trade_Gothic_Text_Bold_16_150_0_700": {
        fontFamily: "var(--font-trade-bold-new), sans-serif",
        fontSize: "1rem",
        fontWeight: 700,
        lineHeight: "150%",
        letterSpacing: "0"
    },
    ".Trade_Gothic_Text_Bold_14_150_0_300": {
        fontFamily: "var(--font-trade-bold-new), sans-serif",
        fontSize: "0.875rem",
        fontWeight: 300,
        lineHeight: "150%",
        letterSpacing: "0"
    },
    ".Trade_Gothic_Text_Bold_14_150_0_700": {
        fontFamily: "var(--font-trade-bold-new), sans-serif",
        fontSize: "0.875rem",
        fontWeight: 700,
        lineHeight: "150%",
        letterSpacing: "0"
    },
    ".Trade_Gothic_Text_14_140_0_300": {
        fontFamily: "var(--font-trade-light-new), sans-serif",
        fontSize: "0.875rem",
        fontWeight: 300,
        lineHeight: "140%",
        letterSpacing: "0"
    },
    ".Trade_Gothic_Text_12_150_0_300": {
        fontFamily: "var(--font-trade-light-new), sans-serif",
        fontSize: "0.75rem",
        fontWeight: 300,
        lineHeight: "150%",
        letterSpacing: "0"
    },
    ".Trade_Gothic_Text_12_150_0_300_uppercase": {
        fontFamily: "var(--font-trade-light-new), sans-serif",
        fontSize: "0.75rem",
        fontWeight: 300,
        lineHeight: "150%",
        letterSpacing: "0",
        textTransform: "uppercase"
    },
    ".Trade_Gothic_Text_10_150_0_300": {
        fontFamily: "var(--font-trade-light-new), sans-serif",
        fontSize: "0.625rem",
        fontWeight: 300,
        lineHeight: "150%",
        letterSpacing: "0"
    },
    ".Trim_Mono_POC_Text_32_130_-4_400_uppercase": {
        fontFamily: "var(--font-trim-regular-new), sans-serif",
        fontSize: "2rem",
        fontWeight: 400,
        lineHeight: "130%",
        letterSpacing: "-0.04em",
        textTransform: "uppercase"
    },
    ".Trim_Mono_POC_Text_10_130_-3_400_uppercase": {
        fontFamily: "var(--font-trim-regular-new), sans-serif",
        fontSize: "0.625rem",
        fontWeight: 400,
        lineHeight: "130%",
        letterSpacing: "-0.03em",
        textTransform: "uppercase"
    },
    ".Trim_Mono_POC_Text_12_130_-3_400_uppercase": {
        fontFamily: "var(--font-trim-regular-new), sans-serif",
        fontSize: "0.75rem",
        fontWeight: 400,
        lineHeight: "130%",
        letterSpacing: "-0.03em",
        textTransform: "uppercase"
    },
    ".Trim_Mono_POC_Header_96_90_10_300_uppercase": {
        fontFamily: "var(--font-trim-light-new), sans-serif",
        fontSize: "6rem",
        fontWeight: 300,
        lineHeight: "90%",
        letterSpacing: "-0.1em",
        textTransform: "uppercase"
    },
    ".Trim_Mono_POC_Header_64_100_4_300_uppercase": {
        fontFamily: "var(--font-trim-light-new), sans-serif",
        fontSize: "4rem",
        fontWeight: 300,
        lineHeight: "100%",
        letterSpacing: "-0.06em",
        textTransform: "uppercase"
    },
    ".Trim_Mono_POC_Header_56_110_3_300_uppercase": {
        fontFamily: "var(--font-trim-light-new), sans-serif",
        fontSize: "3.5rem",
        fontWeight: 300,
        lineHeight: "110%",
        letterSpacing: "-0.06em",
        textTransform: "uppercase"
    },
    ".Trim_Mono_POC_Header_48_110_3_300_uppercase": {
        fontFamily: "var(--font-trim-light-new), sans-serif",
        fontSize: "3rem",
        fontWeight: 300,
        lineHeight: "110%",
        letterSpacing: "-0.06em",
        textTransform: "uppercase"
    },
    ".Trim_Mono_POC_Header_40_110_2_300_uppercase": {
        fontFamily: "var(--font-trim-light-new), sans-serif",
        fontSize: "2.5rem",
        fontWeight: 300,
        lineHeight: "110%",
        letterSpacing: "-0.06em",
        textTransform: "uppercase"
    },
    ".Trim_Mono_POC_Header_32_130_1_300_uppercase": {
        fontFamily: "var(--font-trim-light-new), sans-serif",
        fontSize: "2rem",
        fontWeight: 300,
        lineHeight: "130%",
        letterSpacing: "-0.04em",
        textTransform: "uppercase"
    },
    ".Trim_Mono_POC_Header_24_130_1_300_uppercase": {
        fontFamily: "var(--font-trim-light-new), sans-serif",
        fontSize: "1.5rem",
        fontWeight: 300,
        lineHeight: "130%",
        letterSpacing: "-0.04em",
        textTransform: "uppercase"
    },
    ".Trim_Mono_POC_Header_20_130_1_300_uppercase": {
        fontFamily: "var(--font-trim-light-new), sans-serif",
        fontSize: "1.25rem",
        fontWeight: 300,
        lineHeight: "130%",
        letterSpacing: "-0.04em",
        textTransform: "uppercase"
    },
    ".Trim_Mono_POC_Text_18_130_-3_300_uppercase": {
        fontFamily: "var(--font-trim-light-new), sans-serif",
        fontSize: "1.125rem",
        fontWeight: 300,
        lineHeight: "130%",
        letterSpacing: "-0.03em",
        textTransform: "uppercase"
    },
    ".Trim_Mono_POC_Text_16_130_0_300_uppercase": {
        fontFamily: "var(--font-trim-light-new), sans-serif",
        fontSize: "1rem",
        fontWeight: 300,
        lineHeight: "130%",
        letterSpacing: "0",
        textTransform: "uppercase"
    },
    ".Trim_Mono_POC_Text_16_130_0_500_uppercase": {
        fontFamily: "var(--font-trim-medium-new), sans-serif",
        fontSize: "1rem",
        fontWeight: 500,
        lineHeight: "130%",
        letterSpacing: "0",
        textTransform: "uppercase"
    },
    ".Trim_Mono_POC_Text_15_130_7_500_uppercase": {
        fontFamily: "var(--font-trim-medium-new), sans-serif",
        fontSize: "0.9375rem",
        fontWeight: 500,
        lineHeight: "130%",
        letterSpacing: "-0.75px;",
        textTransform: "uppercase"
    },
    ".Trim_Mono_POC_Text_15_130_0_300_uppercase": {
        fontFamily: "var(--font-trim-light-new), sans-serif",
        fontSize: "0.9375rem",
        fontWeight: 300,
        lineHeight: "130%",
        letterSpacing: "0",
        textTransform: "uppercase"
    },
    ".Trim_Mono_POC_Text_15_130_-5_400_uppercase": {
        fontFamily: "var(--font-trim-regular-new), sans-serif",
        fontSize: "0.9375rem",
        fontWeight: 400,
        lineHeight: "130%",
        letterSpacing: "-0.05em",
        textTransform: "uppercase"
    },
    ".Trim_Mono_POC_Text_15_130_-5_500_uppercase": {
        fontFamily: "var(--font-trim-medium-new), sans-serif",
        fontSize: "0.9375rem",
        fontWeight: 500,
        lineHeight: "130%",
        letterSpacing: "-0.05em",
        textTransform: "uppercase"
    },
    ".Trim_Mono_POC_Text_14_130_0_500_uppercase": {
        fontFamily: "var(--font-trim-medium-new), sans-serif",
        fontSize: "0.875rem",
        fontWeight: 500,
        lineHeight: "130%",
        letterSpacing: "-0.05em",
        textTransform: "uppercase"
    },
    ".Trim_Mono_POC_Text_16_130_-5_500_uppercase": {
        fontFamily: "var(--font-trim-medium-new), sans-serif",
        fontSize: "1rem",
        fontWeight: 500,
        lineHeight: "130%",
        letterSpacing: "-0.05em",
        textTransform: "uppercase"
    },
    ".Trim_Mono_POC_Text_20_130_-5_400_uppercase": {
        fontFamily: "var(--font-trim-regular-new), sans-serif",
        fontSize: "1.25rem",
        fontWeight: 400,
        lineHeight: "130%",
        letterSpacing: "-0.05em",
        textTransform: "uppercase"
    },
    ".Trim_Mono_POC_Text_14_130_-5_400_uppercase": {
        fontFamily: "var(--font-trim-regular-new), sans-serif",
        fontSize: "0.875rem",
        fontWeight: 400,
        lineHeight: "130%",
        letterSpacing: "-0.05em",
        textTransform: "uppercase"
    },
    ".Trim_Mono_POC_Text_14_130_-5_300_uppercase": {
        fontFamily: "var(--font-trim-light-new), sans-serif",
        fontSize: "0.875rem",
        fontWeight: 300,
        lineHeight: "130%",
        letterSpacing: "-0.05em",
        textTransform: "uppercase"
    },
    ".Trim_Mono_POC_Text_12_130_0_400_uppercase": {
        fontFamily: "var(--font-trim-regular-new), sans-serif",
        fontSize: "0.75rem",
        fontWeight: 400,
        lineHeight: "130%",
        letterSpacing: "0",
        textTransform: "uppercase"
    },
    ".Trim_Mono_POC_Text_12_130_6_400_uppercase": {
        fontFamily: "var(--font-trim-regular-new), sans-serif",
        fontSize: "0.75rem",
        fontWeight: 400,
        lineHeight: "130%",
        letterSpacing: "-0.6px",
        textTransform: "uppercase"
    },
    ".Trim_Mono_POC_Text_12_130_0_300_uppercase": {
        fontFamily: "var(--font-trim-light-new), sans-serif",
        fontSize: "0.75rem",
        fontWeight: 300,
        lineHeight: "130%",
        letterSpacing: "0",
        textTransform: "uppercase"
    },
    ".Trim_Mono_POC_Text_12_130_6_500_uppercase": {
        fontFamily: "var(--font-trim-medium-new), sans-serif",
        fontSize: "0.75rem",
        fontWeight: 500,
        lineHeight: "130%",
        letterSpacing: "-0.6px",
        textTransform: "uppercase"
    },
    ".Trim_Mono_POC_Text_10_130_3_400_uppercase": {
        fontFamily: "var(--font-trim-regular-new), sans-serif",
        fontSize: "0.625rem",
        fontWeight: 400,
        lineHeight: "130%",
        letterSpacing: "-0.3px",
        textTransform: "uppercase"
    },
    ".Trim_Mono_POC_Text_12_130_-3_300_uppercase": {
        fontFamily: "var(--font-trim-regular-new), sans-serif",
        fontSize: "0.75rem",
        fontWeight: 300,
        lineHeight: "130%",
        letterSpacing: "-0.6px",
        textTransform: "uppercase"
    },
    // Bold fonts
    ".Trade_Gothic_Header_96_100_-1_700_uppercase": {
        fontFamily: "var(--font-trade-bold-condensed-new), sans-serif",
        fontWeight: 700,
        fontSize: "6rem",
        lineHeight: "100%",
        letterSpacing: "-0.01em",
        textTransform: "uppercase"
    },
    ".Trade_Gothic_Header_64_100_-1_700_uppercase": {
        fontFamily: "var(--font-trade-bold-condensed-new), sans-serif",
        fontWeight: 700,
        fontSize: "4rem",
        lineHeight: "100%",
        letterSpacing: "-0.01em",
        textTransform: "uppercase"
    },
    ".Trade_Gothic_Header_56_100_0_700_uppercase": {
        fontFamily: "var(--font-trade-bold-condensed-new), sans-serif",
        fontWeight: 700,
        fontSize: "3.5rem",
        lineHeight: "100%",
        letterSpacing: "0",
        textTransform: "uppercase"
    },
    ".Trade_Gothic_Header_48_105_0_700_uppercase": {
        fontFamily: "var(--font-trade-bold-condensed-new), sans-serif",
        fontWeight: 700,
        fontSize: "3rem",
        lineHeight: "105%",
        letterSpacing: "0",
        textTransform: "uppercase"
    },
    ".Trade_Gothic_Header_40_105_0_700_uppercase": {
        fontFamily: "var(--font-trade-bold-condensed-new), sans-serif",
        fontWeight: 700,
        fontSize: "2.5rem",
        lineHeight: "105%",
        letterSpacing: "0",
        textTransform: "uppercase"
    },
    ".Trade_Gothic_Header_32_105_0_700_uppercase": {
        fontFamily: "var(--font-trade-bold-condensed-new), sans-serif",
        fontWeight: 700,
        fontSize: "2rem",
        lineHeight: "105%",
        letterSpacing: "0",
        textTransform: "uppercase"
    },
    ".Trade_Gothic_Header_24_125_0_700_uppercase": {
        fontFamily: "var(--font-trade-bold-condensed-new), sans-serif",
        fontWeight: 700,
        fontSize: "1.5rem",
        lineHeight: "125%",
        letterSpacing: "0",
        textTransform: "uppercase"
    },
    ".Trade_Gothic_Header_24_145_0_700_uppercase": {
        fontFamily: "var(--font-trade-bold-condensed-new), sans-serif",
        fontWeight: 700,
        fontSize: "1.5rem",
        lineHeight: "145%",
        textTransform: "uppercase"
    },
    ".Trim_Mono_Regular_POC_Header_96_90_10_300_uppercase": {
        fontFamily: "var(--font-trim-regular-new), sans-serif",
        fontSize: "6rem",
        fontWeight: 400,
        lineHeight: "90%",
        letterSpacing: "-0.1em",
        textTransform: "uppercase"
    },
    ".Trim_Mono_Regular_POC_Header_64_100_4_300_uppercase": {
        fontFamily: "var(--font-trim-regular-new), sans-serif",
        fontSize: "4rem",
        fontWeight: 400,
        lineHeight: "100%",
        letterSpacing: "-0.06em",
        textTransform: "uppercase"
    },
    ".Trim_Mono_Regular_POC_Header_56_110_3_300_uppercase": {
        fontFamily: "var(--font-trim-regular-new), sans-serif",
        fontSize: "3.5rem",
        fontWeight: 400,
        lineHeight: "110%",
        letterSpacing: "-0.06em",
        textTransform: "uppercase"
    },
    ".Trim_Mono_Regular_POC_Header_48_110_3_300_uppercase": {
        fontFamily: "var(--font-trim-regular-new), sans-serif",
        fontSize: "3rem",
        fontWeight: 400,
        lineHeight: "110%",
        letterSpacing: "-0.06em",
        textTransform: "uppercase"
    },
    ".Trim_Mono_Regular_POC_Header_40_110_2_300_uppercase": {
        fontFamily: "var(--font-trim-regular-new), sans-serif",
        fontSize: "2.5rem",
        fontWeight: 400,
        lineHeight: "110%",
        letterSpacing: "-0.06em",
        textTransform: "uppercase"
    },
    ".Trim_Mono_Regular_POC_Header_32_130_1_400_uppercase": {
        fontFamily: "var(--font-trim-regular-new), sans-serif",
        fontSize: "2rem",
        fontWeight: 400,
        lineHeight: "130%",
        letterSpacing: "-0.04em",
        textTransform: "uppercase"
    },
    ".Trim_Mono_Regular_POC_Header_24_130_1_400_uppercase": {
        fontFamily: "var(--font-trim-regular-new), sans-serif",
        fontSize: "1.5rem",
        fontWeight: 400,
        lineHeight: "130%",
        letterSpacing: "-0.04em",
        textTransform: "uppercase"
    },
    ".Trim_Mono_Regular_POC_Header_20_130_1_300_uppercase": {
        fontFamily: "var(--font-trim-regular-new), sans-serif",
        fontSize: "1.25rem",
        fontWeight: 400,
        lineHeight: "130%",
        letterSpacing: "-0.04em",
        textTransform: "uppercase"
    },
    ".Trim_Mono_POC_Text_18_130_-3_400_uppercase": {
        fontFamily: "var(--font-trim-regular-new), sans-serif",
        fontSize: "1.125rem",
        fontWeight: 400,
        lineHeight: "130%",
        letterSpacing: "-0.03em",
        textTransform: "uppercase"
    }
}
